// FILE: frontend/src/components/WatchStatusSelector.js  (NEW FILE)
// PURPOSE: Manual watch status picker — replaces the old auto-tracking hook.
// Used on MovieDetail page. Always public to everyone (per product spec).
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Check, ChevronDown, Eye, Clock, BookmarkCheck, XCircle, PauseCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const MOVIE_STATUSES = [
  { value: 'plan_to_watch', label: 'Planning to Watch', Icon: Clock,        color: 'text-blue-400',   bg: 'bg-blue-500/10',   border: 'border-blue-500/30' },
  { value: 'watching',      label: 'Watching',           Icon: Eye,          color: 'text-green-400',  bg: 'bg-green-500/10',  border: 'border-green-500/30' },
  { value: 'completed',     label: 'Recently Watched',   Icon: BookmarkCheck,color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30' },
  { value: 'dropped',       label: 'Dropped',            Icon: XCircle,      color: 'text-red-400',    bg: 'bg-red-500/10',    border: 'border-red-500/30' },
];

const TV_STATUSES = [
  { value: 'plan_to_watch', label: 'Planning to Watch', Icon: Clock,        color: 'text-blue-400',   bg: 'bg-blue-500/10',   border: 'border-blue-500/30' },
  { value: 'watching',      label: 'Watching',           Icon: Eye,          color: 'text-green-400',  bg: 'bg-green-500/10',  border: 'border-green-500/30' },
  { value: 'completed',     label: 'Recently Watched',   Icon: BookmarkCheck,color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30' },
  { value: 'on_hold',       label: 'On Hold',            Icon: PauseCircle,  color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/30' },
  { value: 'dropped',       label: 'Dropped',            Icon: XCircle,      color: 'text-red-400',    bg: 'bg-red-500/10',    border: 'border-red-500/30' },
];

export default function WatchStatusSelector({ movie, mediaType, tmdbId }) {
  const [currentStatus, setCurrentStatus] = useState(null);
  const [season, setSeason]               = useState('');
  const [episode, setEpisode]             = useState('');
  const [open, setOpen]                   = useState(false);
  const [saving, setSaving]               = useState(false);
  const [loading, setLoading]             = useState(true);

  const isTV      = mediaType === 'tv';
  const statuses  = isTV ? TV_STATUSES : MOVIE_STATUSES;
  const activeObj = statuses.find(s => s.value === currentStatus?.status);

  // Load existing status on mount
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(
          `${BACKEND_URL}/api/watch-status/check/${tmdbId}`,
          { withCredentials: true }
        );
        if (res.data?.status) {
          setCurrentStatus(res.data);
          setSeason(res.data.season || '');
          setEpisode(res.data.episode || '');
        }
      } catch { /* not logged in */ }
      finally { setLoading(false); }
    };
    if (tmdbId) load();
  }, [tmdbId]);

  const handleSelect = async (statusValue) => {
    setSaving(true);
    try {
      const payload = {
        tmdb_id:     parseInt(tmdbId),
        title:       movie?.title || movie?.name || 'Unknown',
        poster_path: movie?.poster_path || null,
        media_type:  isTV ? 'tv' : 'movie',
        status:      statusValue,
        season:      (isTV && season) ? parseInt(season) : null,
        episode:     (isTV && episode) ? parseInt(episode) : null,
      };
      const res = await axios.post(
        `${BACKEND_URL}/api/watch-status`,
        payload,
        { withCredentials: true }
      );
      setCurrentStatus(res.data);
      toast.success(`Marked as "${statuses.find(s => s.value === statusValue)?.label}"`);
      setOpen(false);
    } catch (err) {
      if (err.response?.status === 401) toast.error('Please login to track');
      else toast.error('Failed to save status');
    } finally { setSaving(false); }
  };

  const handleRemove = async () => {
    setSaving(true);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/watch-status/${tmdbId}`,
        { withCredentials: true }
      );
      setCurrentStatus(null);
      setSeason('');
      setEpisode('');
      toast.success('Status removed');
      setOpen(false);
    } catch { toast.error('Failed to remove'); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/10 text-sm text-muted-foreground">
      <Loader2 className="w-4 h-4 animate-spin" />
      Loading…
    </div>
  );

  return (
    <div className="relative inline-block">
      {/* Trigger button */}
      <button
        onClick={() => setOpen(prev => !prev)}
        className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm transition-all active:scale-95 border ${
          activeObj
            ? `${activeObj.bg} ${activeObj.color} ${activeObj.border}`
            : 'bg-white/8 text-white border-white/15 hover:bg-white/12'
        }`}
      >
        {saving
          ? <Loader2 className="w-4 h-4 animate-spin" />
          : activeObj
            ? <activeObj.Icon className="w-4 h-4" />
            : <Eye className="w-4 h-4" />
        }
        {activeObj ? activeObj.label : 'Track Status'}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute left-0 top-full mt-2 bg-card/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl z-30 w-72 overflow-hidden animate-in slide-in-from-top-2 duration-150">
          <div className="p-2">
            {statuses.map(({ value, label, Icon, color, bg, border }) => (
              <button
                key={value}
                onClick={() => handleSelect(value)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  currentStatus?.status === value
                    ? `${bg} ${color} ${border} border`
                    : 'text-gray-300 hover:bg-white/5 border border-transparent'
                }`}
              >
                <Icon className={`w-4 h-4 flex-shrink-0 ${currentStatus?.status === value ? color : 'text-muted-foreground'}`} />
                {label}
                {currentStatus?.status === value && (
                  <Check className={`w-3.5 h-3.5 ml-auto ${color}`} />
                )}
              </button>
            ))}
          </div>

          {/* TV progress tracker */}
          {isTV && currentStatus?.status && ['watching', 'on_hold'].includes(currentStatus.status) && (
            <div className="border-t border-white/8 px-4 py-3 space-y-3">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Progress</p>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="text-[11px] text-muted-foreground mb-1 block">Season</label>
                  <input
                    type="number"
                    min="1"
                    value={season}
                    onChange={e => setSeason(e.target.value)}
                    placeholder="—"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary/50 transition-colors"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-[11px] text-muted-foreground mb-1 block">Episode</label>
                  <input
                    type="number"
                    min="1"
                    value={episode}
                    onChange={e => setEpisode(e.target.value)}
                    placeholder="—"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary/50 transition-colors"
                  />
                </div>
              </div>
              <button
                onClick={() => handleSelect(currentStatus.status)}
                disabled={saving}
                className="w-full py-2 bg-primary/20 text-primary border border-primary/30 rounded-xl text-sm font-bold hover:bg-primary/30 transition-colors"
              >
                {saving ? 'Saving…' : 'Save Progress'}
              </button>
            </div>
          )}

          {/* Remove option */}
          {currentStatus?.status && (
            <div className="border-t border-white/8 p-2">
              <button
                onClick={handleRemove}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-red-400 hover:bg-red-500/10 transition-colors"
              >
                <XCircle className="w-4 h-4" /> Remove from tracking
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
